import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import StylesAPI from "../api/StylesAPI";

import type { ProjectStyles } from "../api/StylesAPI.types";

const EMPTY_STYLES: ProjectStyles = { fonts: { title: null, text: null } };

export interface UseProjectStylesReturn {
  styles: ProjectStyles;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  refetch: () => void;
  saveStyles: (styles: ProjectStyles) => Promise<void>;
}

export function useProjectStyles(projectSlug: string): UseProjectStylesReturn {
  const queryClient = useQueryClient();

  const projectStyleQuery = StylesAPI.generateQuery({
    fn: StylesAPI.getProjectStyles,
    params: [projectSlug],
  });

  const {
    data: projectStyleData,
    isLoading,
    isLoadingError,
    refetch,
  } = useQuery(projectStyleQuery);

  const saveMutation = useMutation({
    mutationFn: async (styles: ProjectStyles) =>
      StylesAPI.updateProjectStyles({ projectSlug, styles }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: projectStyleQuery.queryKey,
      });
    },
  });

  const saveStyles = async (styles: ProjectStyles): Promise<void> => {
    await saveMutation.mutateAsync(styles);
  };

  return {
    styles: projectStyleData ?? EMPTY_STYLES,
    isLoading,
    isError: isLoadingError,
    isSaving: saveMutation.isLoading,
    refetch: (): void => {
      refetch().catch(() => undefined);
    },
    saveStyles,
  };
}
